import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import { RateLine } from "components/Rates";
import { Popup } from "components/popups/Popup";
import React from "react";
import useTranslation from "utils/hooks/useTranslation";

type SatisfactionRow = {
  id?: number | string;
  key?: number | string;
  createdBy_libelle?: string;
  environnement_id?: number | string;
  satisfied?: string;
  data?: {
    ACCESS_FACILITY?: number;
    ERGONOMY?: number;
    SIMPLICITY?: number;
    FIABILITY?: number;
    RAPIDITY?: number;
    LISIBILITY?: number;
    INFORMATIONS?: number;
    DESIGN?: number;
    TOOL_GIVE_HELP_SATISFACTION?: string;
    GIVE_IT_HELP_SATISFACTION?: string;
    GIVE_IT_HELP_PLUS_SATISFACTION?: string;
    SUGGESTION?: string;
    [key: string]: any;
  };
  [key: string]: any;
};

type SatisfactionViewPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  row?: SatisfactionRow | null;
};

const RATING_FIELDS: Array<{ key: string; labelKey: string }> = [
  { key: "ACCESS_FACILITY", labelKey: "ACCESS_FACILITY" },
  { key: "ERGONOMY", labelKey: "ERGONOMY" },
  { key: "SIMPLICITY", labelKey: "SIMPLICITY" },
  { key: "FIABILITY", labelKey: "FIABILITY" },
  { key: "RAPIDITY", labelKey: "RAPIDITY" },
  { key: "LISIBILITY", labelKey: "LISIBILITY" },
  { key: "INFORMATIONS", labelKey: "INFORMATIONS" },
  { key: "DESIGN", labelKey: "DESIGN" },
];

/** Choice fields: value is a locale/translation key (e.g. "YES", "NO") */
const CHOICE_FIELDS: Array<{ key: string; labelKey: string }> = [
  { key: "TOOL_GIVE_HELP_SATISFACTION", labelKey: "TOOL_GIVE_HELP_SATISFACTION" },
  { key: "GIVE_IT_HELP_SATISFACTION", labelKey: "GIVE_IT_HELP_SATISFACTION" },
];

/** Free-text / textarea fields */
const TEXT_FIELDS: Array<{ key: string; labelKey: string }> = [
  { key: "GIVE_IT_HELP_PLUS_SATISFACTION", labelKey: "GIVE_IT_HELP_PLACEHOLDER" },
  { key: "SUGGESTION", labelKey: "SUGGESTION" },
];

const LABEL_WRAP_STYLE: React.CSSProperties = {
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  lineHeight: 1.35,
};

const LABEL_LEFT_COL_STYLE: React.CSSProperties = {
  ...LABEL_WRAP_STYLE,
  minWidth: 260,
  maxWidth: 520,
};

const SatisfactionViewPopup: React.FC<SatisfactionViewPopupProps> = ({ isOpen, onClose, row }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const nested = row?.data ?? row;

  return (
    <Popup
      title={t("Satisfaction Form")}
      onClose={onClose}
      portal
      fixSize="medium"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose}>
              {t("Back")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="30"
        data-height="autofit"
      >
        {/* Rating fields */}
        <group data-direction="column" data-gap="12">
          {RATING_FIELDS.map(({ key, labelKey }) => {
            const val = nested?.[key];
            if (val == null) return null;
            return (
              <group key={key} data-direction="column" data-gap="6">
                <RateLine
                  name={labelKey}
                  value={Number(val)}
                  onChange={() => {}}
                  disabled
                />
              </group>
            );
          })}
        </group>

        {/* Choice / yes-no fields */}
        <group data-direction="column" data-gap="12">
          {CHOICE_FIELDS.map(({ key, labelKey }) => {
            const val = nested?.[key];
            return (
              <group key={key} data-direction="column" data-gap="4">
                <Input
                  dataLength="autofit"
                  label={t(labelKey)}
                  type="text"
                  size="large"
                  value={val == null || val === "" ? "" : t(String(val))}
                  disabled
                  translateLabel={false}
                />
              </group>
            );
          })}
        </group>

        {/* Text / choice fields */}
        <group data-direction="column" data-gap="12">
          {TEXT_FIELDS.map(({ key, labelKey }) => {
            const val = nested?.[key];
            return (
              <group key={key} data-direction="column" data-gap="4">
                <Input
                  dataLength="autofit"
                  label={t(labelKey)}
                  type="textarea"
                  size="large"
                  value={val == null || val === "" ? "" : String(val)}
                  disabled
                  translateLabel={false}
                />
              </group>
            );
          })}
        </group>
      </group>
    </Popup>
  );
};

export default SatisfactionViewPopup;
